/**
 * FN-FE-SUB-06 — Könyvelő Dashboard + Affiliate Tracking
 */
import { useState, useEffect } from 'react';
import { get } from '../../lib/api';

function formatHuf(filler: number): string {
  return Math.round(filler / 100).toLocaleString('hu-HU');
}
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}

interface DashboardData {
  activePartnerships: number;
  totalLifetimeCommissionFiller: number;
  nextPayout: { amountFiller: number };
  partnerships: Array<{ tenantName: string; qualifiedAt: string | null; commissionEndsAt: string | null; lockedIn: boolean }>;
}

interface Commission {
  id: string; commission_amount_filler: string; period_starts_at: string; period_ends_at: string; paid_at: string | null; payment_method: string | null; tenant_name: string;
}

export default function AccountantDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const dashData = await get<DashboardData>('/affiliate/dashboard');
        setData(dashData);

        const commData = await get<any>('/affiliate/commissions');
        setCommissions(commData.data || []);
      } catch { /* */ }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div className="p-8 text-center">Betöltés...</div>;

  const stats = data || { activePartnerships: 0, totalLifetimeCommissionFiller: 0, nextPayout: { amountFiller: 0 }, partnerships: [] };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#14233F]">Könyvelő Dashboard</h1>
          <p className="text-gray-600 mt-1">Ügyfeleid és jutalékaid áttekintése.</p>
        </div>
        <button onClick={() => setShowInvite(true)} className="px-4 py-2 bg-[#F47B20] text-white rounded-lg hover:bg-[#e06b15]">+ Új ügyfél meghívása</button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon="👥" label="Aktív ügyfelek" value={String(stats.activePartnerships)} />
        <StatCard icon="💰" label="Havi jutalék-becslés" value={`${formatHuf(stats.nextPayout.amountFiller)} Ft`} />
        <StatCard icon="📦" label="Összesen kifizetve" value={`${formatHuf(stats.totalLifetimeCommissionFiller)} Ft`} />
        <StatCard icon="📅" label="Következő kifizetés" value="Hó 5." subtext="Automatikus" />
      </div>

      {/* Partnerships table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-lg">Ügyfelek</h2>
        </div>
        {stats.partnerships.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">Még nincs ügyfeled.</p>
            <p className="text-sm mt-1">Hívd meg első ügyfeledet a „+ Új ügyfél meghívása" gombbal.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50">
                <th className="px-6 py-3">Cég</th>
                <th className="px-6 py-3">Qualified</th>
                <th className="px-6 py-3">Jutalék vége</th>
                <th className="px-6 py-3">Lock-in</th>
              </tr>
            </thead>
            <tbody>
              {stats.partnerships.map((p, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium">{p.tenantName}</td>
                  <td className="px-6 py-3">
                    {p.qualifiedAt ? <span className="text-green-600">✓ {formatDate(p.qualifiedAt)}</span> : <span className="text-gray-400">Várakozik</span>}
                  </td>
                  <td className="px-6 py-3">{formatDate(p.commissionEndsAt)}</td>
                  <td className="px-6 py-3">{p.lockedIn ? <span className="text-green-600">✓</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Commission history */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-lg">Jutalék-történet</h2>
        </div>
        {commissions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Még nincs jutalék-bejegyzés.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50">
                <th className="px-6 py-3">Ügyfél</th>
                <th className="px-6 py-3">Periódus</th>
                <th className="px-6 py-3">Összeg</th>
                <th className="px-6 py-3">Kifizetve</th>
                <th className="px-6 py-3">Mód</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map(c => (
                <tr key={c.id} className="border-b border-gray-100">
                  <td className="px-6 py-3">{c.tenant_name}</td>
                  <td className="px-6 py-3">{formatDate(c.period_starts_at)} – {formatDate(c.period_ends_at)}</td>
                  <td className="px-6 py-3 font-medium">{formatHuf(Number(c.commission_amount_filler))} Ft</td>
                  <td className="px-6 py-3">{c.paid_at ? <span className="text-green-600">{formatDate(c.paid_at)}</span> : <span className="text-yellow-600">Függőben</span>}</td>
                  <td className="px-6 py-3">{c.payment_method === 'bank_transfer' ? 'Bank' : c.payment_method === 'modulum_invoice_credit' ? 'Credit' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div className="overlay show" style={{display:"flex",padding:"20px"}} onClick={() => setShowInvite(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-h">
              <h3>Új ügyfél meghívása</h3>
              <button className="x" onClick={() => setShowInvite(false)}>✕</button>
            </div>
            <div className="modal-b">
              <div className="space-y-4">
                <div>
                  <label >Cég-tulajdonos e-mailje *</label>
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="w-full border rounded-lg p-2" placeholder="tulaj@ceg.hu" />
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <p>A meghívóhoz egy referral-kódot generálunk. Az ügyfél regisztrációkor automatikusan hozzád lesz társítva.</p>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setShowInvite(false)} className="flex-1 py-2 border rounded-lg">Mégse</button>
                <button onClick={() => { setShowInvite(false); }} className="flex-1 py-2 bg-[#14233F] text-white rounded-lg">Meghívó küldése</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, subtext }: { icon: string; label: string; value: string; subtext?: string }) {
  return (
    <div className="p-4 bg-white rounded-xl border border-gray-200">
      <div className="flex items-center gap-2 text-gray-600 text-sm">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-[#14233F]">{value}</div>
      {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
    </div>
  );
}
